import * as Yup from "yup";
import {
  addressSchema,
  countrySchema,
  itemTypeSchema,
  stateSchema,
} from "./validations";
export const phoneRegex =
  /^(\+?\d{1,4}[\s-]?)?(\(?\d{2,4}\)?[\s-]?)?[\d\s-]{5,15}$/;

export const shipFromSchema = Yup.object().shape({
  isExistingAddress: Yup.boolean(),

  shouldSaveAddress: Yup.boolean(),

  existingAddress: Yup.object().when("isExistingAddress", {
    is: true,
    then: Yup.object()
      .required("existing address is required")
      .test(
        "is-not-empty",
        "Please select an existing address",
        (value) => value && Object.keys(value).length > 0
      ),
    otherwise: Yup.object().nullable(),
  }),

  name: Yup.string().when("isExistingAddress", {
    is: (value) => value !== true,
    then: Yup.string()
      .required("Name is required")
      .min(3, "Name must be at least 3 characters")
      .max(100, "Name must be at most 100 characters"),
  }),

  address1: Yup.string().when("isExistingAddress", {
    is: (value) => value !== true,
    then: Yup.string().required("Address is required"),
  }),

  address2: Yup.string(),

  city: Yup.string().when("isExistingAddress", {
    is: (value) => value !== true,
    then: Yup.string().required("City is required"),
  }),

  state: Yup.object().when("isExistingAddress", {
    is: (value) => value !== true,
    then: stateSchema,
  }),

  country: Yup.object().when("isExistingAddress", {
    is: (value) => value !== true,
    then: countrySchema,
  }),

  zip: Yup.string().when(["isExistingAddress", "country"], {
    is: (value) => value !== true,
    then: Yup.string().required("ZIP / Postal code is required"),
    otherwise: Yup.string().notRequired(),
  }),

  phone: Yup.string().when("isExistingAddress", {
    is: (value) => value !== true,
    then: Yup.string()
      .required("Phone number is required")
      .matches(phoneRegex, "Enter a valid phone number"),
  }),

  email: Yup.string().when("isExistingAddress", {
    is: (value) => value !== true,
    then: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
  }),
});

export const shipToSchema = addressSchema;

export const getItemSchema = (isInternationalShipment) =>
  Yup.object().shape({
    isImperial: Yup.boolean(),

    type: itemTypeSchema.required("Item type is required"),

    length: Yup.string().when("type", {
      is: (type) => type?.name === "Package",
      then: Yup.string().required("Length is required for packages"),
      otherwise: Yup.string(),
    }),

    width: Yup.string().when("type", {
      is: (type) => type?.name === "Package",
      then: Yup.string().required("Width is required for packages"),
      otherwise: Yup.string(),
    }),

    height: Yup.string().when("type", {
      is: (type) => type?.name === "Package",
      then: Yup.string().required("Height is required for packages"),
      otherwise: Yup.string(),
    }),

    weight: Yup.string().when("type", {
      is: (type) => type?.name === "Package",
      then: Yup.string().required("Weight is required for packages"),
      otherwise: Yup.string(),
    }),

    invoiceCurrency: Yup.object()
      .shape({
        id: Yup.string().required(),
        name: Yup.string().required(),
      })
      .when([], {
        is: () => isInternationalShipment,
        then: (schema) =>
          schema.required(
            "Invoice currency is required for international shipments"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),

    invoice: getInvoiceSchema(isInternationalShipment),
  });

const rowSchema = Yup.object().shape({
  description: Yup.string().required("Description is required"),

  hsCode: Yup.string()
    .matches(
      /^\d{4}\.\d{2}\.\d{2}\.\d{2}$/,
      "HS / HTS code must be in the format 4506.10.00.00"
    )
    .required("HS / HTS code is required"),

  originCountry: Yup.object()
    .shape({
      id: Yup.string(),
      name: Yup.string(),
    })
    .test("country-filled", "Origin Country is required", (value) => {
      return value && value.id && value.name;
    }),

  unitPrice: Yup.number()
    .typeError("Unit price must be a number")
    .positive("Unit price must be positive")
    .required("Unit price is required"),

  quantity: Yup.number()
    .typeError("Quantity must be a number")
    .integer("Quantity must be an integer")
    .positive("Quantity must be positive")
    .required("Quantity is required"),
});

const getInvoiceSchema = (isInternationalShipment) =>
  Yup.array().when([], {
    is: () => isInternationalShipment,
    then: Yup.array()
      .of(rowSchema)
      .min(1, "At least one invoice item is required"),
    otherwise: Yup.array().max(
      0,
      "Invoice must be empty for domestic shipments"
    ),
  });

export const checkoutSchema = Yup.object({
  selectedPaymentMethod: Yup.object()
    .shape({
      id: Yup.string().required("Please select a payment method"),
    })
    .required("Payment method is required")
    .nullable(), // allow null at first
});